import { themeColors } from './themeColors';
import colorContrast from 'color-contrast';
import { extendTheme } from '@chakra-ui/react';
import { BaseThemeColors } from './interfaces/baseThemeColors';

export const breakpoints: Record<string, number> = {
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
  '2xl': 1536,
};

export const chakraTheme = (colors: BaseThemeColors) => {
  return extendTheme({
    fonts: {
      body: 'Ambit-Regular, Inter, sans-serif',
    },
    colors: {
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      ...themeColors,
    },
    components: {
      Button: {
        baseStyle: {
          fontFamily: 'Ambit-Regular, Inter, sans-serif',
        },
        variants: {
          solid: {
            backgroundColor: colors.primary,
            color: colorContrast('#fff', colors.primary) > 3 ? 'white' : '#1E252B',
            fill: colorContrast('#fff', colors.primary) > 3 ? 'white' : '#1E252B',
          },
          outline: {
            backgroundColor: 'white',
            borderColor: colors.primary,
            color: colors.primary,
            fill: colors.primary,
            borderWidth: '2px',
          },
          ghost: {
            backgroundColor: 'white',
            boxShadow: 'none',
          },
        },
      },
      Input: {
        variants: {
          outline: {
            field: {
              backgroundColor: '#fff',
              borderWidth: '1px',
              borderColor: '#CBBFE6',
              color: themeColors.typography.darkest,
              fontWeight: 400,
              fontSize: '14px',
              borderRadius: '10px',
              transition: 'border 0.3s',
              fontFamily: 'Ambit-Regular, Inter, sans-serif',
              _placeholder: {
                color: themeColors.typography.darkest,
                opacity: '0.5',
              },
              _focus: {
                boxShadow: 'none',
                color: themeColors.typography.darkest,
              },
              _hover: {
                color: themeColors.typography.darkest,
              },
            },
          },
          'outline-secondary': {
            field: {
              backgroundColor: '#fff',
              borderWidth: '1px',
              borderColor: '#CBBFE6',
              color: themeColors.typography.darkest,
              fontWeight: 400,
              fontSize: '14px',
              borderRadius: '10px',
              transition: 'border 0.3s',
              fontFamily: 'Ambit-Regular, Inter, sans-serif',
              _placeholder: {
                color: themeColors.typography.darkest,
              },
              _focus: {
                boxShadow: 'none',
                color: themeColors.typography.darkest,
              },
              _hover: {
                color: themeColors.typography.darkest,
              },
              _disabled: {},
            },
          },
        },
      },
      Textarea: {
        baseStyle: {
          fontFamily: 'Ambit-Regular, Inter, sans-serif',
        },
        variants: {
          outline: {
            backgroundColor: '#fff',
            borderWidth: '1px',
            borderColor: '#CBBFE6',
            color: themeColors.typography.darkest,
            fontWeight: 400,
            fontSize: '14px',
            borderRadius: '10px',
            transition: 'border 0.3s',
            fontFamily: 'Ambit-Regular, Inter, sans-serif',
            _placeholder: {
              color: themeColors.typography.darkest,
              opacity: '0.5',
            },
            _focus: {
              boxShadow: 'none',
              color: themeColors.typography.darkest,
            },
            _hover: {
              color: themeColors.typography.darkest,
            },
          },
        },
      },
      Select: {
        variants: {
          outline: {
            field: {
              borderRadius: '10px',
              borderColor: themeColors.gray.lightest,
              fontSize: '12px',
              fontWeight: 400,
              fontFamily: 'Ambit-Regular, Inter, sans-serif',
              color: themeColors.typography.dark,
              _focus: {
                boxShadow: 'none',
                userSelect: 'none',
                borderColor: themeColors.gray.lightest,
              },
              _placeholder: {
                fontSize: '12px',
                fontWeight: 400,
                fontFamily: 'Ambit-Regular, Inter, sans-serif',
              },
            },
          },
        },
      },
      Modal: {
        baseStyle: {
          dialog: {
            borderRadius: '30px',
            padding: '20px',
          },
        },
      },
      FormLabel: {
        baseStyle: {
          fontFamily: 'Ambit-Regular, Inter, sans-serif',
          color: 'typography.darkest',
          fontSize: '12px',
        },
      },
      Badge: {
        variants: {
          solid: {
            fontFamily: 'Ambit-Regular, Inter, sans-serif',
            textTransform: 'inherit',
            fontSize: '13px',
            letterSpacing: '1px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '50px',
            padding: '0 10px',
          },
        },
      },
      Radio: {
        baseStyle: {
          control: {
            transition: 'all 0.3s',
            borderColor: '#3D0E6B',
            _hover: {
              backgroundColor: '#FFB6BF',
              borderColor: '#FFB6BF',
            },
            _checked: {
              backgroundColor: '#3D0E6B',
              borderColor: '#3D0E6B',
              _hover: {
                backgroundColor: '#3D0E6B',
                borderColor: '#3D0E6B',
              },
            },
          },
          label: {
            color: '#3D0E6B',
            fontSize: '12px !important',
          },
        },
        sizes: {
          sm: {
            control: {
              width: '12px',
              height: '12px',
            },
            label: {
              fontSize: '12px !important',
            },
          },
          md: {
            control: {
              width: '16px',
              height: '16px',
            },
            label: {
              fontSize: '14px !important',
            },
          },
          lg: {
            control: {
              width: '20px',
              height: '20px',
            },
            label: {
              fontSize: '16px !important',
            },
          },
        },
      },
      Checkbox: {
        baseStyle: {
          control: {
            borderColor: '#3D0E6B',
            borderRadius: '4px',
            _checked: {
              bg: '#FFB6BF',
              borderColor: '#FFB6BF',
              color: '#3D0E6B',
              _hover: {
                bg: '#FFB6BF',
              },
            },
            _hover: {
              borderColor: '#3D0E6B',
            },
            _focus: {
              boxShadow: 'none',
            },
          },
          label: {
            color: '#3D0E6B',
            lineHeight: '13px',
          },
        },
        sizes: {
          sm: {
            control: {
              width: '10px',
              height: '10px',
            },
            label: {
              fontSize: '12px !important',
            },
          },
          md: {
            control: {
              width: '16px',
              height: '16px',
            },
            label: {
              fontSize: '14px !important',
            },
          },
          lg: {
            control: {
              width: '20px',
              height: '20px',
            },
            label: {
              fontSize: '16px !important',
            },
          },
        },
        variants: {
          solid: {},
        },
      },
    },
    breakpoints: {
      sm: `${breakpoints.sm}px`,
      md: `${breakpoints.md}px`,
      lg: `${breakpoints.lg}px`,
      xl: `${breakpoints.xl}px`,
      '2xl': `${breakpoints['2xl']}px`,
    },
  });
};
